<template>
	<div>
		<h3>Intégration API</h3>
		<p class="mt-2 mb-4">
			Une clé API vous permet d'utiliser l'API Rankwell depuis un script ou un
			assistant IA (Claude Code, n8n, Python…) sans vous connecter au dashboard.
			Chaque clé vous identifie personnellement et peut être révoquée
			individuellement.
		</p>

		<!-- The key, shown once -->
		<vx-card v-if="newKey" class="mb-6" title="Votre nouvelle clé API">
			<vs-alert color="warning" icon="warning" class="mb-4" active="true">
				Copiez-la maintenant : elle ne sera plus jamais affichée. En cas de
				perte, révoquez-la et générez-en une nouvelle.
			</vs-alert>
			<div class="flex items-center mb-4">
				<vs-input class="w-full mr-2" :value="newKey.key" readonly />
				<vs-button icon="content_copy" @click="copy(newKey.key, 'Clé API copiée.')"
					>Copier</vs-button
				>
			</div>
			<p class="mb-2"><strong>Votre user_id :</strong> {{ newKey.user_id }}</p>
			<vs-divider />
			<p class="mb-2">
				Prompt à copier dans Claude Code (ou tout autre assistant) :
			</p>
			<pre class="prompt">{{ promptText }}</pre>
			<vs-button
				class="mt-2"
				type="border"
				icon="content_copy"
				@click="copy(promptText, 'Prompt copié.')"
				>Copier le prompt</vs-button
			>
		</vx-card>

		<!-- Generate -->
		<div class="flex items-end mb-6">
			<vs-input
				class="mr-2"
				label="Nom de la clé (optionnel)"
				placeholder="Claude Code"
				v-model="name"
			/>
			<vs-button :disabled="isLoading" @click="generate">Générer une clé</vs-button>
		</div>

		<!-- Existing keys -->
		<vs-table :data="keys" noDataText="Aucune clé générée.">
			<template slot="thead">
				<vs-th>Nom</vs-th>
				<vs-th>Clé</vs-th>
				<vs-th>Créée le</vs-th>
				<vs-th>Dernière utilisation</vs-th>
				<vs-th>Statut</vs-th>
				<vs-th></vs-th>
			</template>
			<template slot-scope="{ data }">
				<vs-tr v-for="k in data" :key="k.id" :data="k">
					<vs-td>{{ k.name || "—" }}</vs-td>
					<vs-td><code>{{ k.prefix }}…</code></vs-td>
					<vs-td>{{ formatDate(k.created_at) }}</vs-td>
					<vs-td>{{ k.last_used_at ? formatDate(k.last_used_at) : "jamais" }}</vs-td>
					<vs-td>
						<vs-chip :color="k.revoked_at ? 'danger' : 'success'">{{
							k.revoked_at ? "Révoquée" : "Active"
						}}</vs-chip>
					</vs-td>
					<vs-td>
						<vs-button
							v-if="!k.revoked_at"
							color="danger"
							type="flat"
							size="small"
							@click="confirmRevoke(k)"
							>Révoquer</vs-button
						>
					</vs-td>
				</vs-tr>
			</template>
		</vs-table>

		<vs-alert color="primary" icon="info" class="mt-6" active="true">
			Une clé donne les mêmes droits d'écriture que votre compte. Ne la partagez
			pas, ne la committez pas dans un dépôt. Documentation complète :
			<a href="https://rankwell.one/api/docs/" target="_blank"
				>rankwell.one/api/docs</a
			>
		</vs-alert>
	</div>
</template>

<script>
import moment from "moment";

export default {
	name: "ApiIntegration",
	data() {
		return {
			keys: [],
			name: "",
			newKey: null,
			isLoading: false,
		};
	},
	computed: {
		/** Pasted into Claude Code so it knows the base URL, the header and the rules. */
		promptText() {
			if (!this.newKey) return "";
			return [
				"Tu peux utiliser l'API Rankwell.",
				"",
				`Base URL : ${this.apiBase}`,
				"Spécification OpenAPI : https://rankwell.one/api/docs/openapi.yaml",
				`Authentification : en-tête  X-Api-Key: ${this.newKey.key}`,
				`Cette clé appartient à l'utilisateur id ${this.newKey.user_id}.`,
				"",
				"Les GET sont ouverts ; POST/PUT/PATCH/DELETE et /cron/* exigent l'en-tête.",
				"Toute erreur a la forme {\"err\": \"...\"}. 400/401/404 = corriger la requête,",
				"500 = réessayer avec backoff (3 fois max).",
				"Les dates s'envoient en ISO 8601 avec fuseau horaire.",
			].join("\n");
		},
		apiBase() {
			return (process.env.VUE_APP_API_URL || "https://rankwell.one/api/").replace(/\/$/, "");
		},
	},
	created() {
		this.fetchKeys();
	},
	methods: {
		async fetchKeys() {
			try {
				const res = await this.$http.get("apikeys");
				this.keys = res.data;
			} catch (err) {
				this.notifyError(err, "Impossible de charger vos clés API.");
			}
		},
		async generate() {
			this.isLoading = true;
			try {
				const res = await this.$http.post("apikeys", { name: this.name });
				this.newKey = res.data;
				this.name = "";
				await this.fetchKeys();
			} catch (err) {
				this.notifyError(err, "La clé n'a pas pu être générée.");
			} finally {
				this.isLoading = false;
			}
		},
		confirmRevoke(key) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: "Révoquer cette clé ?",
				text: `${key.name || key.prefix} cessera immédiatement de fonctionner. Cette action est irréversible.`,
				acceptText: "Révoquer",
				cancelText: "Annuler",
				accept: () => this.revoke(key.id),
			});
		},
		async revoke(id) {
			try {
				await this.$http.delete(`apikeys/${id}`);
				if (this.newKey && this.newKey.id === id) this.newKey = null;
				await this.fetchKeys();
				this.$vs.notify({ title: "Succès", text: "Clé révoquée." });
			} catch (err) {
				this.notifyError(err, "La clé n'a pas pu être révoquée.");
			}
		},
		copy(text, message) {
			// clipboard API needs a secure context; the textarea fallback covers the rest.
			const done = () => this.$vs.notify({ title: "Succès", text: message });
			if (navigator.clipboard && window.isSecureContext) {
				navigator.clipboard.writeText(text).then(done);
				return;
			}
			const el = document.createElement("textarea");
			el.value = text;
			document.body.appendChild(el);
			el.select();
			document.execCommand("copy");
			document.body.removeChild(el);
			done();
		},
		formatDate(date) {
			return date ? moment(date).format("DD/MM/YYYY HH:mm") : "—";
		},
		notifyError(err, text) {
			console.log(err);
			const serverMsg = err && err.response && err.response.data && err.response.data.err;
			this.$vs.notify({ title: "Erreur", text: serverMsg || text, color: "danger" });
		},
	},
};
</script>

<style scoped>
.prompt {
	white-space: pre-wrap;
	word-break: break-all;
	background: rgba(0, 0, 0, 0.04);
	border-radius: 4px;
	padding: 0.75rem;
	font-size: 0.8rem;
	max-height: 260px;
	overflow: auto;
}
</style>
